// v2 arctic fox — the true winter morph: a compact deep-furred body on short
// thick snow legs with furry paw cuffs, a round face with full cheek fluff, the
// small rounded heat-saving ears, a short muzzle, and the enormous brush tail
// curling low around the haunches. Two coats: snow white and the blue-grey
// morph. Ported 1:1 from age-of-mcp's v2/arctic-fox.ts, using the library's
// shared `critter` (quadLegs) and `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, critter, wildland, type Part } from "../../lib";

const COATS = [
  { coat: "#f4f7fa", under: "#e6ebf0", leg: "#e9edf1", nose: "#1a1614" },
  { coat: "#a8b2bc", under: "#c2cad2", leg: "#9aa4ae", nose: "#14110e" },
];

function foxParts(v: number): Part[] {
  const c = COATS[v % COATS.length]!;
  const parts: Part[] = critter.quadLegs({
    dx: 0.04, dz: 0.02, bodyY: 0.055, rTop: 0.008, rFoot: 0.0062,
    color: c.leg, hoof: { color: c.coat, r: 0.008, h: 0.01 },
  });
  parts.push(
    // compact winter-coat body + deep chest
    { geometry: new THREE.IcosahedronGeometry(0.036, 0), color: c.coat,
      position: [0, 0.055, 0], scale: [1.55, 1.0, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.027, 0), color: c.under,
      position: [0.035, 0.05, 0], scale: [1.0, 1.05, 0.95] },
    // round head, short muzzle, dark nose
    { geometry: new THREE.IcosahedronGeometry(0.0205, 0), color: c.coat,
      position: [0.064, 0.075, 0], scale: [1.1, 1.0, 1.0] },
    { geometry: new THREE.ConeGeometry(0.008, 0.018, 4), color: c.coat,
      position: [0.085, 0.069, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.003, 0), color: c.nose,
      position: [0.094, 0.069, 0] },
    // the huge brush — root, plume, tip — curled low to one side
    wildland.tube([-0.05, 0.052, 0], [-0.075, 0.04, 0.01], 0.011, 0.014, c.coat),
    { geometry: new THREE.IcosahedronGeometry(0.0185, 0), color: c.under,
      position: [-0.088, 0.036, 0.016], scale: [1.5, 1.0, 1.0] },
    { geometry: new THREE.IcosahedronGeometry(0.011, 0), color: c.coat,
      position: [-0.108, 0.034, 0.02] },
  );
  for (const s of [1, -1] as const) {
    parts.push(
      // cheek fluff + small rounded ears
      { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.under,
        position: [0.066, 0.066, s * 0.015] },
      { geometry: new THREE.IcosahedronGeometry(0.0065, 0), color: c.coat,
        position: [0.057, 0.098, s * 0.0125], scale: [0.8, 1.0, 0.6] },
    );
  }
  parts.push(...wildland.eyes(0.071, 0.081, 0.0105, 0.003, "#22180e"));
  return parts;
}

export const arcticFoxV2 = defineModel({
  id: "arctic-fox-v2",
  batchId: "fable",
  name: "Arctic fox (v2)",
  tags: ["creature","arctic","fox","low-poly","age-of-mcp","fable"],
  summary: "Low-poly arctic fox.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => foxParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => foxParts(1) },
  ],
  animations: [],
});
